import { useTargetOS, useClickOutside } from '@/global/hooks'
import {
  getRandomizesFindOutAlice,
  getRandomizesSocialMedias,
  OTHERS,
  SOCIAL_MEDIAS
} from '@/modules/simulator/functions'
import { WAutocomplete, WTextfield } from '@alice-health/wonderland-react'
import {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { WAutocompleteCustomEvent } from '@alice-health/wonderland'
import { useFormContext } from 'react-hook-form'
import { SimulatorContext } from '@/modules/simulator/contexts'

type AutocompleteOpenedKey = 'findOutAliceSelect' | 'socialMediaSelect'

const randomFindOutAlice = getRandomizesFindOutAlice()
const randomSocialMedias = getRandomizesSocialMedias()

const FindOutAliceSelect: FunctionComponent = () => {
  const autocompleteRef = useRef(null)
  const [autocompleteOpened, setAutocompleteOpened] =
    useState<AutocompleteOpenedKey>()

  const { simulatorWrapperRef } = useContext(SimulatorContext)

  const {
    register,
    setValue,
    getValues,
    clearErrors,
    watch,
    formState: { errors }
  } = useFormContext<{
    otherField?: string
    findOutAliceSelect?: string
    socialMediaSelect?: string
  }>()

  useEffect(() => {
    const isTablet = window.innerWidth >= 630 && window.innerWidth < 1024

    if (simulatorWrapperRef && autocompleteOpened && !isTablet) {
      simulatorWrapperRef.current?.scrollTo({
        top: simulatorWrapperRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, [autocompleteOpened])

  useClickOutside({
    ref: autocompleteRef,
    callback: () => {
      setAutocompleteOpened(undefined)
    }
  })

  const os = useTargetOS()

  const findOutAliceSelect = watch('findOutAliceSelect')

  const handleAutoCompleteOpened = (key: AutocompleteOpenedKey) => {
    if (autocompleteOpened === key) {
      setAutocompleteOpened(undefined)
    } else if (!autocompleteOpened || autocompleteOpened !== key) {
      setAutocompleteOpened(key)
    }
  }

  const getAutocompleteMargin = (key: AutocompleteOpenedKey) => {
    return autocompleteOpened === key && os !== 'iOS' ? 'mb-[254px]' : ''
  }

  return (
    <div className='flex flex-col gap-4'>
      <div
        className='flex flex-col gap-4'
        ref={autocompleteRef}
      >
        <div
          className={getAutocompleteMargin('findOutAliceSelect')}
          data-testid='findOutAliceSelect-wrapper'
        >
          <WAutocomplete
            placeholder='Como conheceu a Alice?'
            items={randomFindOutAlice}
            value={getValues('findOutAliceSelect')}
            onClick={() => handleAutoCompleteOpened('findOutAliceSelect')}
            onWSelect={(event: WAutocompleteCustomEvent<string>) =>
              setValue('findOutAliceSelect', event.detail)
            }
          />
        </div>

        {findOutAliceSelect === SOCIAL_MEDIAS ? (
          <div
            className={getAutocompleteMargin('socialMediaSelect')}
            data-testid='socialMediaSelect-wrapper'
          >
            <WAutocomplete
              placeholder='Qual rede social?'
              items={randomSocialMedias}
              value={getValues('socialMediaSelect')}
              onClick={() => handleAutoCompleteOpened('socialMediaSelect')}
              onWSelect={(event: WAutocompleteCustomEvent<string>) =>
                setValue('socialMediaSelect', event.detail)
              }
            />
          </div>
        ) : null}
      </div>

      {findOutAliceSelect === OTHERS ? (
        <WTextfield
          {...register('otherField')}
          placeholder='Especifique'
          invalid={!!errors.otherField}
          errorText={errors.otherField?.message}
          onWChange={ev => setValue('otherField', ev.detail)}
          onInput={() => clearErrors('otherField')}
        />
      ) : null}
    </div>
  )
}

export type { AutocompleteOpenedKey }

export { FindOutAliceSelect }
